import { useState, useEffect } from 'react';
import { useNavigate }       from 'react-router-dom';
import {
  ArrowLeft, Save, GitMerge, Loader2, AlertCircle, CheckCircle2,
} from 'lucide-react';

import { cardReconciliationApi } from '@/api/cardReconciliation.api';
import { cardAcquirerApi }       from '@/api/cardAcquirer.api';
import { AppRoutes }             from '@/constants/routes';
import { Spinner }               from '@/components/ui/Spinner';
import { cn }                    from '@/utils/cn';

import type { CardAcquirerListDto, CreateCardReconciliationRequest } from '@/types/cardReconciliation.types';

const CARD_TYPES = [
  { value: '',           label: 'All Card Types' },
  { value: 'Visa',       label: 'Visa' },
  { value: 'Mastercard', label: 'Mastercard' },
  { value: 'Amex',       label: 'American Express' },
  { value: 'UnionPay',   label: 'UnionPay' },
  { value: 'JCB',        label: 'JCB' },
  { value: 'Discover',   label: 'Discover' },
  { value: 'Other',      label: 'Other' },
];

// ── Field helper ──────────────────────────────────────────────────────────────
interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}
const Field = ({ label, required, hint, children }: FieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const inputCls = cn(
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors',
  'placeholder:text-slate-400',
);

// ── Page ──────────────────────────────────────────────────────────────────────

export const CardReconciliationFormPage = () => {
  const navigate = useNavigate();

  const [acquirers,      setAcquirers]     = useState<CardAcquirerListDto[]>([]);
  const [loadingAcq,     setLoadingAcq]    = useState(true);
  const [saving,         setSaving]        = useState(false);
  const [error,          setError]         = useState<string | null>(null);
  const [success,        setSuccess]       = useState(false);

  // ── Form state ────────────────────────────────────────────────────────────
  const today = new Date().toISOString().substring(0, 10);
  const [reconciliationNo,        setReconciliationNo]       = useState('');
  const [financialYearCode,       setFinancialYearCode]      = useState('');
  const [financialPeriodCode,     setFinancialPeriodCode]    = useState('');
  const [cardAcquirerId,          setCardAcquirerId]         = useState('');
  const [cardType,                setCardType]               = useState('');
  const [dateFrom,                setDateFrom]               = useState(today);
  const [dateTo,                  setDateTo]                 = useState(today);
  const [remarks,                 setRemarks]                = useState('');

  // ── Load acquirers ────────────────────────────────────────────────────────
  useEffect(() => {
    cardAcquirerApi.getList(true)
      .then(res => {
        const list = res.data?.data ?? [];
        setAcquirers(list);
        if (list.length === 1) setCardAcquirerId(list[0].id);
      })
      .catch(err => console.error('Failed to load acquirers', err))
      .finally(() => setLoadingAcq(false));
  }, []);

  // ── Auto-generate recon number ────────────────────────────────────────────
  useEffect(() => {
    if (!financialPeriodCode || !cardAcquirerId) return;
    const acq   = acquirers.find(a => a.id === cardAcquirerId);
    const code  = acq?.acquirerCode ?? 'CRD';
    const date  = new Date().toISOString().replace(/[-T:.Z]/g, '').slice(0, 12);
    setReconciliationNo(`CRD-${code}-${financialPeriodCode}-${date}`);
  }, [financialPeriodCode, cardAcquirerId, acquirers]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);

    if (!reconciliationNo.trim())   { setError('Reconciliation number is required.'); return; }
    if (!financialYearCode.trim())  { setError('Financial year code is required.'); return; }
    if (!financialPeriodCode.trim()){ setError('Financial period code is required.'); return; }
    if (!cardAcquirerId)            { setError('Card acquirer is required.'); return; }
    if (!dateFrom || !dateTo)       { setError('Date range is required.'); return; }
    if (dateFrom > dateTo)          { setError('Date From cannot be after Date To.'); return; }

    const req: CreateCardReconciliationRequest = {
      reconciliationNo:    reconciliationNo.trim(),
      financialYearCode:   financialYearCode.trim().toUpperCase(),
      financialPeriodCode: financialPeriodCode.trim().toUpperCase(),
      cardAcquirerId,
      cardType:            cardType || null,
      dateFrom,
      dateTo,
      remarks:             remarks.trim() || null,
    };

    setSaving(true);
    try {
      const res = await cardReconciliationApi.create(req);
      const newId = (res.data as unknown as { data: { id: string } })?.data?.id
                  ?? (res.data?.data as unknown as { id: string })?.id;
      setSuccess(true);
      setTimeout(() => {
        if (newId) {
          navigate(AppRoutes.CARD_RECONCILIATION_DETAIL.replace(':id', newId));
        } else {
          navigate(AppRoutes.CARD_RECONCILIATIONS);
        }
      }, 900);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Failed to create reconciliation.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadingAcq) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  if (acquirers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 dark:text-gray-400 gap-4">
        <AlertCircle size={40} className="text-amber-400" />
        <p className="font-medium">No card acquirers configured</p>
        <p className="text-sm text-slate-400">Set up at least one card acquirer before creating a reconciliation.</p>
        <button
          onClick={() => navigate(AppRoutes.CARD_ACQUIRER_NEW)}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Add Card Acquirer
        </button>
      </div>
    );
  }

  const selectedAcquirer = acquirers.find(a => a.id === cardAcquirerId);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.CARD_RECONCILIATIONS)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              <GitMerge size={20} className="text-teal-600 dark:text-teal-400" />
              <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">New Card Reconciliation</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Define the scope and date range for this reconciliation session
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(AppRoutes.CARD_RECONCILIATIONS)}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || success}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                success
                  ? 'bg-green-600 text-white'
                  : 'bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50',
              )}
            >
              {saving  ? <Loader2 size={15} className="animate-spin" /> :
               success ? <CheckCircle2 size={15} /> :
                         <Save size={15} />}
              {saving ? 'Creating…' : success ? 'Created!' : 'Create Reconciliation'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Error banner */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
            </div>
          )}

          {/* ── Reconciliation Identity ───────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Reconciliation Identity
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Reconciliation Number" required hint="Auto-generated — edit if needed">
                <input
                  value={reconciliationNo}
                  onChange={e => setReconciliationNo(e.target.value)}
                  placeholder="CRD-ACQUIRER-PERIOD-…"
                  className={inputCls}
                />
              </Field>
              <Field label="Financial Year Code" required hint="e.g. FY2025">
                <input
                  value={financialYearCode}
                  onChange={e => setFinancialYearCode(e.target.value.toUpperCase())}
                  placeholder="FY2025"
                  className={inputCls}
                />
              </Field>
              <Field label="Financial Period Code" required hint="e.g. FY2025-05 or 2025-05">
                <input
                  value={financialPeriodCode}
                  onChange={e => setFinancialPeriodCode(e.target.value.toUpperCase())}
                  placeholder="FY2025-05"
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          {/* ── Scope ────────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Reconciliation Scope
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Card Acquirer" required>
                <select
                  value={cardAcquirerId}
                  onChange={e => setCardAcquirerId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">— Select acquirer —</option>
                  {acquirers.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.acquirerCode} — {a.acquirerName}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Card Type" hint="Leave blank to reconcile all card types">
                <select
                  value={cardType}
                  onChange={e => setCardType(e.target.value)}
                  className={inputCls}
                >
                  {CARD_TYPES.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Date From" required>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Date To" required>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>

            {selectedAcquirer && (
              <div className="mt-4 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-sm text-teal-700 dark:text-teal-300">
                <span className="font-medium">{selectedAcquirer.acquirerName}</span>
                {' · '}MDR {(selectedAcquirer.mdrRate * 100).toFixed(3)}%
                {' · '}Settlement +{selectedAcquirer.settlementDelayDays}d
              </div>
            )}
          </div>

          {/* ── Remarks ──────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
              Remarks
            </h2>
            <textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              rows={3}
              placeholder="Optional notes about this reconciliation session…"
              className={cn(inputCls, 'resize-none')}
            />
          </div>

        </div>
      </div>
    </div>
  );
};
